<template>
  <v-card
    v-if="cameraDialogStore.camera.exid"
    v-resize-observer="onResize"
    :flat="inline"
    :loading="cameraDialogStore.loading"
    class="camera-details__content overflow-hidden"
    tile
  >
    <v-card-title class="pa-0 pl-2 camera-details__header">
      <CameraDialogHeader
        v-if="isCameraSelected"
        :camera="cameraDialogStore.camera"
        :inline="inline"
        @close="$emit('close')"
      />
    </v-card-title>
    <v-divider />

    <v-container class="pa-0 ma-0 overflow e-max-h-screen e-h-full" fluid>
      <ETabs
        v-model="cameraDialogStore.tab"
        active-class="blue lighten-5"
        class="camera-details__tabs custom-tabs"
        :vertical="$vuetify.breakpoint.mdAndUp"
        @tab-selected="onTabChange"
      >
        <div v-for="tab in tabs" :key="tab.value" :name="tab.name">
          <Summary
            v-if="showTab(tab, CAMERA_TABS.SUMMARY)"
            @close="$emit('close')"
          />
          <SnapshotExtractions
            v-if="showTab(tab, CAMERA_TABS.EXTRACTIONS)"
            :exid="cameraDialogStore.camera.exid"
          />
          <BrainTool v-if="showTab(tab, CAMERA_TABS.RECOGNITION)" />
          <Demo v-if="showTab(tab, CAMERA_TABS.DEMO)" :camera="camera" />
          <Milestones v-if="showTab(tab, CAMERA_TABS.MILESTONES)" />
          <DangerZone v-if="showTab(tab, CAMERA_TABS.DANGER_ZONE)" />
          <ZohoDeskTicketsTable
            v-if="showTab(tab, CAMERA_TABS.TICKETS)"
            :cameras-exids="[cameraDialogStore.camera.exid]"
            :height="height"
            class="px-5 pt-5"
          />
        </div>
      </ETabs>
    </v-container>
  </v-card>
</template>

<script lang="ts">
import CameraDialogHeader from "@/components/cameraDialog/CameraDialogHeader"
import { CAMERA_TABS } from "@/components/constants.js"
import Summary from "@/components/cameraTabs/Summary"
import SnapshotExtractions from "@/components/cameraTabs/SnapshotExtractions"
import Demo from "@/components/cameraTabs/Demo"
import BrainTool from "@/components/cameraTabs/BrainTool.vue"
import DangerZone from "@/components/cameraTabs/DangerZone"
import Milestones from "@/components/cameraTabs/Milestones.vue"
import ZohoDeskTicketsTable from "@/components/zohoDesk/ZohoDeskTicketsTable.vue"
import { mapStores } from "pinia"
import { useCameraDialogStore } from "@/stores/cameraDialog"
import { useLayoutStore } from "@/stores/layout"
import { PERMISSIONS } from "@/constants/permissions"

export default {
  name: "CameraDialogContent",
  components: {
    Summary,
    CameraDialogHeader,
    SnapshotExtractions,
    BrainTool,
    Demo,
    DangerZone,
    Milestones,
    ZohoDeskTicketsTable,
  },
  props: {
    camera: {
      type: [String, Object],
      required: true,
    },
    inline: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isCameraSelected: false,
      PERMISSIONS,
      CAMERA_TABS,
      height: undefined,
    }
  },
  computed: {
    ...mapStores(useCameraDialogStore, useLayoutStore),
    simNumber(): string {
      return this.cameraDialogStore.camera?.simNumber
        ? this.cameraDialogStore.camera.simNumber
        : ""
    },
    tabs() {
      const tabConfig = [
        {
          name: "Summary",
          value: CAMERA_TABS.SUMMARY,
          isAllowed: this.$permissions.user.can(PERMISSIONS.CAMERAS.VIEW),
        },
        {
          name: "Extractions",
          value: CAMERA_TABS.EXTRACTIONS,
          isAllowed: this.$permissions.user.can(
            PERMISSIONS.CAMERAS.VIEW_EXTRACTIONS
          ),
        },
        {
          name: "BrainTool",
          value: CAMERA_TABS.RECOGNITION,
          isAllowed: this.$permissions.user.can(PERMISSIONS.CAMERAS.VIEW),
        },
        {
          name: "Demo",
          value: CAMERA_TABS.DEMO,
          isAllowed: this.$permissions.user.can(PERMISSIONS.DEMOS.VIEW),
        },
        {
          name: "Milestones",
          value: CAMERA_TABS.MILESTONES,
          isAllowed: this.$permissions.user.can(PERMISSIONS.CAMERAS.VIEW),
        },
        {
          name: "Danger Zone",
          value: CAMERA_TABS.DANGER_ZONE,
          isAllowed: this.$permissions.user.can(PERMISSIONS.CAMERAS.DELETE),
        },
        {
          name: "Tickets",
          value: CAMERA_TABS.TICKETS,
          isAllowed: this.$permissions.user.can(PERMISSIONS.CAMERAS.VIEW),
        },
      ]

      return tabConfig.filter((tab) => tab.isAllowed)
    },
  },
  watch: {
    camera: {
      immediate: true,
      async handler(cam = {}) {
        const exid = typeof cam === "object" ? cam?.exid : cam
        if (exid) {
          this.isCameraSelected = false
          await this.cameraDialogStore.selectCamera(exid)
          this.isCameraSelected = true
        }
      },
    },
  },
  methods: {
    onTabChange(tab) {
      this.updateSubQuery({ subQueryObj: { tab } })
    },
    showTab(currentTab, tabId) {
      return currentTab.value === tabId
    },
    onResize({ contentRect }) {
      this.height = contentRect.height - 135
    },
  },
}
</script>

<style lang="scss">
@import "~vuetify/src/styles/styles.sass";
@import "assets/vars";

.camera-details {
  overflow: hidden;

  @media #{map-get($display-breakpoints, 'lg-and-up')} {
    max-width: calc(100vw - #{$sidebar-width});
    margin-left: $sidebar-width !important;
  }

  &__header {
    box-shadow: 0px 5px 12px -13px #0000007a;
    z-index: 1;
  }

  &__content {
    overflow: hidden;
  }

  &__tabs {
    & > .v-tabs-bar {
      border-right: 1px solid #e0e0e0;
      box-shadow: 5px 0px 11px -12px #000000b5;
      z-index: 1;
    }
    & > .v-tabs-items {
      overflow-y: auto;
      max-height: calc(100vh - 57px);
      height: calc(100vh - 57px);
    }
    .v-tab {
      text-transform: none;
    }

    @media #{map-get($display-breakpoints, 'sm-and-down')} {
      .v-tabs-bar {
        border-bottom: thin solid #e0e0e0;
      }
    }
  }

  &--fullscreen {
    position: relative;
    @media #{map-get($display-breakpoints, 'lg-and-up')} {
      max-width: calc(100vw - #{$sidebar-width--sm});
      margin-left: $sidebar-width--sm !important;
    }
  }

  &--fullscreen-no-side {
    position: relative;
    @media #{map-get($display-breakpoints, 'lg-and-up')} {
      max-width: 100%;
      margin-left: 0 !important;
    }
  }
}

.camera-tab-fullscreen {
  display: none;
}
</style>
